import React, { useRef, useEffect, useState } from 'react';
import type { Todo } from '../types';
import type { FormData } from '../utils/formUtils';

interface TodoFormParentTaskProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  todo?: Todo;
  parentTask: Todo | null;
  filteredParentOptions: Todo[];
  selectedParentName: string;
  onParentClick?: (todo: Todo) => void;
}

const TodoFormParentTask: React.FC<TodoFormParentTaskProps> = ({
  formData,
  setFormData,
  todo,
  parentTask,
  filteredParentOptions,
  selectedParentName,
  onParentClick,
}) => {
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const parentDropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside parent dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (parentDropdownRef.current && !parentDropdownRef.current.contains(event.target as Node)) {
        setShowParentDropdown(false);
      }
    };

    if (showParentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showParentDropdown]);

  // Re-filter based on local search term
  const visibleOptions = parentSearchTerm
    ? filteredParentOptions.filter(t => t.title.toLowerCase().includes(parentSearchTerm.toLowerCase()))
    : filteredParentOptions;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-gray-700">
          Parent Task (Optional)
        </label>
        {todo && parentTask && onParentClick && (
          <button
            type="button"
            onClick={() => onParentClick(parentTask)}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
            title="View current parent task"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Parent Task
          </button>
        )}
      </div>

      {/* Custom searchable dropdown */}
      <div className="relative" ref={parentDropdownRef}>
        <div
          className="input-field cursor-pointer flex items-center justify-between"
          onClick={() => setShowParentDropdown(!showParentDropdown)}
        >
          <span className={formData.parent_id ? 'text-gray-900' : 'text-gray-500'}>
            {formData.parent_id ? `📋 ${selectedParentName}` : '🔗 No Parent Task'}
          </span>
          <div className="flex items-center gap-1">
            {formData.parent_id && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormData({ ...formData, parent_id: '' });
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                title="Clear parent task"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {showParentDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={parentSearchTerm}
                onChange={(e) => setParentSearchTerm(e.target.value)}
                placeholder="Search parent tasks..."
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Options list */}
            <div className="max-h-48 overflow-y-auto">
              <div
                className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center"
                onClick={() => {
                  setFormData({ ...formData, parent_id: '' });
                  setShowParentDropdown(false);
                  setParentSearchTerm('');
                }}
              >
                🔗 No Parent Task
              </div>
              {visibleOptions.length > 0 ? (
                visibleOptions.map(parentTodo => (
                  <div
                    key={parentTodo.id}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center"
                    onClick={() => {
                      setFormData({ ...formData, parent_id: parentTodo.id });
                      setShowParentDropdown(false);
                      setParentSearchTerm('');
                    }}
                  >
                    📋 {parentTodo.title}
                  </div>
                ))
              ) : parentSearchTerm ? (
                <div className="px-3 py-2 text-sm text-gray-500 italic">
                  No parent tasks found matching &quot;{parentSearchTerm}&quot;
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-1">
        Make this a subtask of another task
        {todo && parentTask && (
          <span className="ml-2 text-blue-600">
            • Currently linked to: {parentTask.title}
          </span>
        )}
      </p>
    </div>
  );
};

export default TodoFormParentTask;
